const MAX_DOWNLOAD_BYTES = 256 * 1024 * 1024;
const DOWNLOAD_MESSAGE = "alg-download-store";
const ACK_TIMEOUT_MS = 5_000;

export class ServiceWorkerDownloadError extends Error {}

function safeFilename(value: string): string {
  const cleaned = value
    .replace(/[\u0000-\u001f\u007f]/g, "")
    .replace(/[\\/]/g, "_")
    .trim()
    .slice(0, 180);
  return cleaned && cleaned !== "." && cleaned !== ".." ? cleaned : "download";
}

function downloadId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/**
 * Give an already authenticated Blob to the controlling service worker and
 * click a normal, same-origin HTTPS attachment URL. No blob: URL is ever used
 * for navigation, so Electron cannot hand one to the operating system.
 */
export async function downloadBlobViaServiceWorker(blob: Blob, filename: string): Promise<void> {
  if (!("serviceWorker" in navigator)) {
    throw new ServiceWorkerDownloadError("Downloads require service-worker support in this app.");
  }
  const controller = navigator.serviceWorker.controller;
  if (!controller) {
    throw new ServiceWorkerDownloadError("Downloads are not ready yet. Reload the app and try again.");
  }
  if (blob.size > MAX_DOWNLOAD_BYTES) {
    throw new ServiceWorkerDownloadError("This file is too large to download in the app (256 MB limit).");
  }

  const id = downloadId();
  const name = safeFilename(filename);
  const channel = new MessageChannel();
  const acknowledged = new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      channel.port1.close();
      reject(new ServiceWorkerDownloadError("The download service did not respond. Reload the app and try again."));
    }, ACK_TIMEOUT_MS);
    channel.port1.onmessage = (event: MessageEvent<unknown>) => {
      window.clearTimeout(timer);
      channel.port1.close();
      const reply = event.data as { ok?: boolean; error?: string } | null;
      if (reply?.ok) resolve();
      else reject(new ServiceWorkerDownloadError(reply?.error || "The download could not be prepared."));
    };
  });

  controller.postMessage(
    { type: DOWNLOAD_MESSAGE, id, name, blob },
    [channel.port2],
  );
  await acknowledged;

  const link = document.createElement("a");
  link.href = `/__alg-download/${encodeURIComponent(id)}`;
  link.download = name;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/** Fetch a local object URL back into its Blob without navigating to it. */
export async function blobFromObjectUrl(url: string): Promise<Blob> {
  const response = await fetch(url);
  if (!response.ok) throw new ServiceWorkerDownloadError(`The attachment could not be read (${response.status}).`);
  return response.blob();
}
